// supabaseClient.js
// Put your Supabase URL and anon key here after creating your project

import { createClient } from '@supabase/supabase-js';

console.log('supabaseClient.js loading');

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase configuration. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.');
}

console.log('Creating Supabase client...');
export const supabase = createClient(supabaseUrl, supabaseAnonKey);
console.log('Supabase client created');

// ============================================
// AUTH HELPERS
// ============================================

export const signUp = async (email, password) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  });
  return { data, error };
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const resetPassword = async (email) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/?reset=true`,
  });
  return { data, error };
};

// LinkedIn OAuth Sign-In (OIDC)
// Returns basic profile data: name, email, profile picture, headline
// Note: Full work history requires LinkedIn Partner Program (enterprise-only)
export const signInWithLinkedIn = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'linkedin_oidc',
    options: {
      redirectTo: `${window.location.origin}/`,
    }
  });
  return { data, error };
};

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange(callback);
};

// ============================================
// PROFILE HELPERS
// ============================================

export const getProfile = async (userId, providedSession = null) => {
  console.log('🔍 getProfile called with userId:', userId, 'session provided:', !!providedSession);

  try {
    // Use provided session or try to get one (but getSession() may hang)
    let session = providedSession;

    if (!session) {
      console.log('🔍 No session provided, attempting getSession()...');
      const result = await Promise.race([
        supabase.auth.getSession(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('getSession timeout')), 3000))
      ]);
      session = result?.data?.session;
    }

    console.log('🔍 Session check:', {
      hasSession: !!session,
      sessionUserId: session?.user?.id,
      hasAccessToken: !!session?.access_token
    });

    if (!session?.access_token) {
      console.error('❌ No valid session/access token');
      return { data: null, error: new Error('No active session') };
    }

    // Use direct fetch with timeout to avoid Supabase client hanging issues
    console.log('🔍 Fetching profile via REST API...');
    const startTime = Date.now();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    try {
      const profileColumns = 'id,name,email,title,target_role,current_salary,target_salary_min,target_salary_max,summary,skills,experiences,current_projects,subscription_status,role_tracks,active_track_id,education,activities';
      const response = await fetch(
        `${supabaseUrl}/rest/v1/profiles?id=eq.${userId}&select=${profileColumns}`,
        {
          method: 'GET',
          headers: {
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
          },
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);
      const elapsed = Date.now() - startTime;
      console.log('🔍 REST API response in', elapsed, 'ms, status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ REST API error:', response.status, errorText);
        return { data: null, error: new Error(`HTTP ${response.status}: ${errorText}`) };
      }

      const rows = await response.json();
      console.log('🔍 REST API result:', {
        rowCount: rows?.length,
        hasData: rows?.length > 0,
        subscriptionStatus: rows?.[0]?.subscription_status
      });

      const data = rows?.[0] || null;

      if (!data) {
        console.warn('⚠️ No profile found for userId:', userId);
        return { data: null, error: null };
      }

      console.log('✅ Profile loaded successfully:', data.name, '| Subscription:', data.subscription_status);
      return { data, error: null };

    } catch (fetchErr) {
      clearTimeout(timeoutId);
      if (fetchErr.name === 'AbortError') {
        console.error('❌ Profile fetch timed out after 8 seconds');
        return { data: null, error: new Error('Profile fetch timeout') };
      }
      throw fetchErr;
    }

  } catch (err) {
    console.error('❌ getProfile exception:', err);
    return { data: null, error: err };
  }
};

export const updateProfile = async (userId, updates) => {
  // Use upsert to create profile if it doesn't exist, or update if it does
  // Note: removed .select().single() as it can cause hangs with RLS
  const { error } = await supabase
    .from('profiles')
    .upsert({ id: userId, ...updates }, { onConflict: 'id' });

  if (error) {
    console.error('updateProfile error:', error);
    return { data: null, error };
  }
  return { data: { id: userId, ...updates }, error: null };
};

// Lazy-load education data - only fetch when needed (e.g., resume generation)
export const getProfileEducation = async (userId) => {
  console.log('📚 getProfileEducation called for userId:', userId);
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('education')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('📚 getProfileEducation error:', error.message);
      return { data: [], error };
    }

    console.log('📚 getProfileEducation success:', data?.education?.length || 0, 'entries');
    return { data: data?.education || [], error: null };
  } catch (e) {
    console.error('📚 getProfileEducation exception:', e.message);
    return { data: [], error: e };
  }
};

// ============================================
// JOBS HELPERS
// ============================================

// Lightweight fetch for dashboard list - excludes heavy columns
// Supports pagination with limit (default 10) and offset
export const getJobsSummary = async (userId, limit = 10, offset = 0) => {
  console.log('📦 getJobsSummary called:', { userId, limit, offset });
  try {
    // Only fetch lightweight columns needed for job list display
    const { data, error, count } = await supabase
      .from('jobs')
      .select('id, company, role, url, status, match_score, confidence, is_target, added_at', { count: 'exact' })
      .eq('user_id', userId)
      .order('added_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('📦 getJobsSummary error:', error.message, error.code, error.details);
      return { data: [], error, totalCount: 0 };
    }

    console.log('📦 getJobsSummary success:', { fetched: data?.length || 0, total: count });
    return { data: data || [], error: null, totalCount: count || 0 };
  } catch (e) {
    console.error('📦 getJobsSummary exception:', e.message);
    return { data: [], error: e, totalCount: 0 };
  }
};

// Full fetch for a single job - includes all heavy columns (analysis, resume, etc)
export const getJobDetails = async (jobId) => {
  console.log('📦 getJobDetails called for jobId:', jobId);
  try {
    const { data, error } = await supabase
      .from('jobs')
      .select('id, user_id, company, role, url, status, match_score, confidence, is_target, analysis, gap_answers, generated_resume, raw_jd, suggested_projects, added_at, updated_at')
      .eq('id', jobId)
      .single();

    if (error) {
      console.error('📦 getJobDetails error:', error.message);
      return { data: null, error };
    }

    console.log('📦 getJobDetails success:', data?.company, data?.role);
    return { data, error: null };
  } catch (e) {
    console.error('📦 getJobDetails exception:', e.message);
    return { data: null, error: e };
  }
};

// Full fetch for all jobs - only use when explicitly needed (e.g., export)
export const getJobs = async (userId) => {
  console.log('📦 getJobs called for userId:', userId);
  try {
    // Use explicit column list with limit to prevent timeout
    const { data, error } = await supabase
      .from('jobs')
      .select('id, user_id, company, role, url, status, match_score, confidence, is_target, analysis, gap_answers, generated_resume, raw_jd, suggested_projects, added_at, updated_at')
      .eq('user_id', userId)
      .order('added_at', { ascending: false })
      .limit(100);

    if (error) {
      console.error('📦 getJobs error:', error.message, error.code, error.details);
      return { data: [], error };
    }

    console.log('📦 getJobs success:', { count: data?.length || 0 });
    if (data?.length > 0) {
      console.log('📦 First job:', data[0]?.company, data[0]?.role);
    }
    return { data: data || [], error: null };
  } catch (e) {
    console.error('📦 getJobs exception:', e.message);
    return { data: [], error: e };
  }
};

export const addJob = async (userId, job) => {
  console.log('💾 addJob called:', { userId, company: job.company, role: job.role });
  const { data, error } = await supabase
    .from('jobs')
    .insert({
      user_id: userId,
      company: job.company,
      role: job.role,
      url: job.url || null,
      // Note: job_posted_date column does not exist in DB yet
      status: job.status || 'analyzing',
      match_score: job.matchScore || 0,
      confidence: job.confidence || 0,
      is_target: job.isTarget || false,
      analysis: job.analysis || {},
      gap_answers: job.gapAnswers || {},
      generated_resume: job.generatedResume || null,
      raw_jd: job.rawJd || null,
      suggested_projects: job.suggestedProjects || [],
    })
    .select()
    .single();
  if (error) {
    console.error('💾 addJob error:', error.message, error.details);
  } else {
    console.log('💾 addJob success:', data?.id);
  }
  return { data, error };
};

export const updateJob = async (jobId, updates) => {
  const { data, error } = await supabase
    .from('jobs')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', jobId)
    .select()
    .single();
  return { data, error };
};

export const deleteJob = async (jobId) => {
  const { error } = await supabase
    .from('jobs')
    .delete()
    .eq('id', jobId);
  return { error };
};

// ============================================
// INSIGHTS HELPERS
// ============================================

export const getInsights = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('insights')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error && error.code === '42P01') {
      return { data: [], error: null };
    }
    return { data: data || [], error };
  } catch (e) {
    console.log('Error fetching insights:', e);
    return { data: [], error: null };
  }
};

export const addInsight = async (userId, insight) => {
  const { data, error } = await supabase
    .from('insights')
    .insert({
      user_id: userId,
      skill: insight.skill,
      context: insight.context,
      source: insight.source || 'gap_analysis',
      job_context: insight.jobContext || null,
    })
    .select()
    .single();
  return { data, error };
};

export const deleteInsight = async (insightId) => {
  const { error } = await supabase
    .from('insights')
    .delete()
    .eq('id', insightId);
  return { error };
};

// ============================================
// SKILL ASSESSMENTS HELPERS
// ============================================

export const getSkillAssessments = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('skill_assessments')
      .select('*')
      .eq('user_id', userId);
    if (error && error.code === '42P01') {
      return { data: [], error: null };
    }
    return { data: data || [], error };
  } catch (e) {
    console.log('Error fetching skill assessments:', e);
    return { data: [], error: null };
  }
};

export const upsertSkillAssessment = async (userId, skill, level, notes = null) => {
  const { data, error } = await supabase
    .from('skill_assessments')
    .upsert({
      user_id: userId,
      skill,
      level,
      notes,
      updated_at: new Date().toISOString(),
    }, {
      onConflict: 'user_id,skill',
    })
    .select()
    .single();
  return { data, error };
};

// ============================================
// USAGE TRACKING HELPERS
// ============================================

export const getUsage = async (userId, month) => {
  const { data, error } = await supabase
    .from('usage')
    .select('*')
    .eq('user_id', userId)
    .eq('month', month)
    .single();
  return { data, error };
};

export const incrementUsage = async (userId, field) => {
  const month = new Date().toISOString().slice(0, 7); // '2026-01'

  // Try to get existing record
  const { data: existing } = await supabase
    .from('usage')
    .select('*')
    .eq('user_id', userId)
    .eq('month', month)
    .single();

  if (existing) {
    // Update existing
    const { data, error } = await supabase
      .from('usage')
      .update({ [field]: existing[field] + 1 })
      .eq('id', existing.id)
      .select()
      .single();
    return { data, error };
  } else {
    // Create new
    const { data, error } = await supabase
      .from('usage')
      .insert({
        user_id: userId,
        month,
        [field]: 1,
      })
      .select()
      .single();
    return { data, error };
  }
};

// ============================================
// SUBSCRIPTION & FREE TIER HELPERS
// ============================================

const FREE_RESUME_LIMIT = 5;      // 5 tailored resumes/month for free users
// Pro = unlimited

const FREE_FETCH_LIMIT = 5;       // 5 auto-fetches/month for free users
// Pro = unlimited

const FREE_ANALYSIS_LIMIT = 5;    // 5 job analyses/month for free users
// Pro/Admin = unlimited

export const getSubscriptionInfo = async (userId) => {
  // Note: Don't use .single() as it can hang when RLS blocks access
  // Try to get usage columns, fall back to just subscription_status if they don't exist
  let { data, error } = await supabase
    .from('profiles')
    .select('subscription_status, is_admin, monthly_resumes_used, usage_reset_month, monthly_fetches_used, fetch_reset_month, monthly_analyses_used, analysis_reset_month')
    .eq('id', userId)
    .limit(1);

  // If columns don't exist, fall back to just subscription_status
  if (error?.code === '42703') {
    console.warn('Usage columns not found, falling back to subscription_status only');
    const fallback = await supabase
      .from('profiles')
      .select('subscription_status')
      .eq('id', userId)
      .limit(1);
    return { data: fallback.data?.[0] || null, error: null };
  }

  return { data: data?.[0] || null, error };
};

// Get effective subscription level
export const getEffectiveSubscription = (subscriptionStatus) => {
  return subscriptionStatus || 'free';
};

// Job analysis - 5/month for free users, unlimited for pro/admin
export const canUserAnalyze = async (userId) => {
  const { data, error } = await getSubscriptionInfo(userId);

  if (error) {
    console.error('Error checking subscription:', error);
    return { canAnalyze: false, remaining: 0, subscriptionStatus: 'free' };
  }

  const subscriptionStatus = data?.subscription_status || 'free';
  const effectiveTier = getEffectiveSubscription(subscriptionStatus);
  const isAdmin = data?.is_admin === true;
  const currentMonth = new Date().toISOString().slice(0, 7);

  // Admin and Pro users have unlimited
  if (isAdmin || effectiveTier === 'pro') {
    return { canAnalyze: true, remaining: -1, subscriptionStatus, effectiveTier, isAdmin };
  }

  // Free users have 5/month
  let monthlyUsed = data?.monthly_analyses_used || 0;
  const usageMonth = data?.analysis_reset_month;

  // Reset if new month
  if (usageMonth !== currentMonth) {
    monthlyUsed = 0;
  }

  const remaining = Math.max(0, FREE_ANALYSIS_LIMIT - monthlyUsed);
  return {
    canAnalyze: remaining > 0,
    remaining,
    subscriptionStatus,
    effectiveTier,
    isAdmin,
    monthlyUsed,
  };
};

// Resume generation is limited by tier
export const canUserGenerateResume = async (userId) => {
  const { data, error } = await getSubscriptionInfo(userId);

  if (error) {
    console.error('Error checking subscription:', error);
    return { canGenerate: false, remaining: 0, subscriptionStatus: 'free' };
  }

  const subscriptionStatus = data?.subscription_status || 'free';
  const effectiveTier = getEffectiveSubscription(subscriptionStatus);
  const currentMonth = new Date().toISOString().slice(0, 7);

  // Pro users have unlimited
  if (effectiveTier === 'pro') {
    return { canGenerate: true, remaining: -1, subscriptionStatus, effectiveTier };
  }

  // Free users have 5/month
  let monthlyUsed = data?.monthly_resumes_used || 0;
  const usageMonth = data?.usage_reset_month;

  if (usageMonth !== currentMonth) {
    monthlyUsed = 0;
  }

  const remaining = Math.max(0, FREE_RESUME_LIMIT - monthlyUsed);
  return {
    canGenerate: remaining > 0,
    remaining,
    subscriptionStatus,
    effectiveTier,
    monthlyUsed,
  };
};

// Track resume generation usage
export const incrementResumesUsed = async (userId) => {
  // Try to get usage columns, handle missing columns gracefully
  let { data: profile, error: selectError } = await supabase
    .from('profiles')
    .select('subscription_status, monthly_resumes_used, usage_reset_month')
    .eq('id', userId)
    .single();

  // If columns don't exist, just get subscription_status
  if (selectError?.code === '42703') {
    const fallback = await supabase
      .from('profiles')
      .select('subscription_status')
      .eq('id', userId)
      .single();
    profile = fallback.data;
  }

  const subscriptionStatus = profile?.subscription_status || 'free';
  const effectiveTier = getEffectiveSubscription(subscriptionStatus);
  const currentMonth = new Date().toISOString().slice(0, 7);

  // Pro users don't need tracking
  if (effectiveTier === 'pro') {
    return { data: profile, error: null, newCount: 0, type: 'pro', remaining: -1 };
  }

  const limit = FREE_RESUME_LIMIT;

  let monthlyUsed = profile?.monthly_resumes_used || 0;
  const usageMonth = profile?.usage_reset_month;

  // Reset if new month
  if (usageMonth !== currentMonth) {
    monthlyUsed = 0;
  }

  const newCount = monthlyUsed + 1;

  // Try to update usage columns, but don't fail if they don't exist
  const { data, error } = await supabase
    .from('profiles')
    .update({
      monthly_resumes_used: newCount,
      usage_reset_month: currentMonth,
    })
    .eq('id', userId)
    .select()
    .single();

  // If update failed due to missing columns, still return success with estimated count
  if (error?.code === '42703') {
    console.warn('Usage columns not found, tracking disabled');
    return { data: profile, error: null, newCount, type: effectiveTier, remaining: limit - newCount };
  }

  return { data, error, newCount, type: effectiveTier, remaining: limit - newCount };
};

// Check if user can auto-fetch job URLs (limited by tier)
export const canUserFetch = async (userId) => {
  const { data, error } = await getSubscriptionInfo(userId);

  if (error) {
    console.error('Error checking subscription:', error);
    return { canFetch: false, remaining: 0, subscriptionStatus: 'free' };
  }

  const subscriptionStatus = data?.subscription_status || 'free';
  const effectiveTier = getEffectiveSubscription(subscriptionStatus);
  const currentMonth = new Date().toISOString().slice(0, 7);

  // Pro users have unlimited
  if (effectiveTier === 'pro') {
    return { canFetch: true, remaining: -1, subscriptionStatus, effectiveTier };
  }

  // Free users have 5/month
  let monthlyUsed = data?.monthly_fetches_used || 0;
  const usageMonth = data?.fetch_reset_month;

  if (usageMonth !== currentMonth) {
    monthlyUsed = 0;
  }

  const remaining = Math.max(0, FREE_FETCH_LIMIT - monthlyUsed);
  return {
    canFetch: remaining > 0,
    remaining,
    subscriptionStatus,
    effectiveTier,
    monthlyUsed,
  };
};

// Track job URL fetch usage
export const incrementFetchesUsed = async (userId) => {
  // Try to get usage columns, handle missing columns gracefully
  let { data: profile, error: selectError } = await supabase
    .from('profiles')
    .select('subscription_status, monthly_fetches_used, fetch_reset_month')
    .eq('id', userId)
    .single();

  // If columns don't exist, just get subscription_status
  if (selectError?.code === '42703') {
    const fallback = await supabase
      .from('profiles')
      .select('subscription_status')
      .eq('id', userId)
      .single();
    profile = fallback.data;
  }

  const subscriptionStatus = profile?.subscription_status || 'free';
  const effectiveTier = getEffectiveSubscription(subscriptionStatus);
  const currentMonth = new Date().toISOString().slice(0, 7);

  // Pro users don't need tracking
  if (effectiveTier === 'pro') {
    return { data: profile, error: null, newCount: 0, type: 'pro', remaining: -1 };
  }

  const limit = FREE_FETCH_LIMIT;
  let monthlyUsed = profile?.monthly_fetches_used || 0;
  const usageMonth = profile?.fetch_reset_month;

  // Reset if new month
  if (usageMonth !== currentMonth) {
    monthlyUsed = 0;
  }

  const newCount = monthlyUsed + 1;

  // Try to update usage columns, but don't fail if they don't exist
  const { data, error } = await supabase
    .from('profiles')
    .update({
      monthly_fetches_used: newCount,
      fetch_reset_month: currentMonth,
    })
    .eq('id', userId)
    .select()
    .single();

  // If update failed due to missing columns, still return success with estimated count
  if (error?.code === '42703') {
    console.warn('Fetch usage columns not found, tracking disabled');
    return { data: profile, error: null, newCount, type: effectiveTier, remaining: limit - newCount };
  }

  return { data, error, newCount, type: effectiveTier, remaining: limit - newCount };
};

// Check if user can access Interview Prep features (Pro only)
export const canAccessInterviewPrep = async (userId) => {
  const { data, error } = await getSubscriptionInfo(userId);
  if (error) return { canAccess: false, subscriptionStatus: 'free' };

  const subscriptionStatus = data?.subscription_status || 'free';
  const effectiveTier = getEffectiveSubscription(subscriptionStatus);

  // Interview prep requires Pro
  return {
    canAccess: effectiveTier === 'pro',
    subscriptionStatus,
    effectiveTier,
    requiredTier: 'pro'
  };
};

// Check if user can access Development features (Pro only)
export const canAccessDevelopment = async (userId) => {
  const { data, error } = await getSubscriptionInfo(userId);
  if (error) return { canAccess: false, subscriptionStatus: 'free' };

  const subscriptionStatus = data?.subscription_status || 'free';
  const effectiveTier = getEffectiveSubscription(subscriptionStatus);

  // Development features require Pro
  return {
    canAccess: effectiveTier === 'pro',
    subscriptionStatus,
    effectiveTier,
    requiredTier: 'pro'
  };
};

// Track job analysis usage
export const incrementAnalysesUsed = async (userId) => {
  // Try to get usage columns, handle missing columns gracefully
  let { data: profile, error: selectError } = await supabase
    .from('profiles')
    .select('subscription_status, is_admin, monthly_analyses_used, analysis_reset_month')
    .eq('id', userId)
    .single();

  // If columns don't exist, just get subscription_status
  if (selectError?.code === '42703') {
    const fallback = await supabase
      .from('profiles')
      .select('subscription_status')
      .eq('id', userId)
      .single();
    profile = fallback.data;
  }

  const subscriptionStatus = profile?.subscription_status || 'free';
  const effectiveTier = getEffectiveSubscription(subscriptionStatus);
  const isAdmin = profile?.is_admin === true;
  const currentMonth = new Date().toISOString().slice(0, 7);

  // Admin and Pro users don't need tracking
  if (isAdmin || effectiveTier === 'pro') {
    return { data: profile, error: null, newCount: 0, type: 'unlimited', remaining: -1 };
  }

  const limit = FREE_ANALYSIS_LIMIT;
  let monthlyUsed = profile?.monthly_analyses_used || 0;
  const usageMonth = profile?.analysis_reset_month;

  // Reset if new month
  if (usageMonth !== currentMonth) {
    monthlyUsed = 0;
  }

  const newCount = monthlyUsed + 1;

  // Try to update usage columns, but don't fail if they don't exist
  const { data, error } = await supabase
    .from('profiles')
    .update({
      monthly_analyses_used: newCount,
      analysis_reset_month: currentMonth,
    })
    .eq('id', userId)
    .select()
    .single();

  // If update failed due to missing columns, still return success with estimated count
  if (error?.code === '42703') {
    console.warn('Analysis usage columns not found, tracking disabled');
    return { data: profile, error: null, newCount, type: effectiveTier, remaining: limit - newCount };
  }

  return { data, error, newCount, type: effectiveTier, remaining: limit - newCount };
};

export const createCheckoutSession = async (userId, userEmail, plan = 'pro', promoCode = null) => {
  console.log('🔵 createCheckoutSession called:', { userId, userEmail, plan, promoCode });
  try {
    const response = await fetch('/api/create-checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, userEmail, plan, promoCode }),
    });

    console.log('🔵 Response status:', response.status);
    const data = await response.json();
    console.log('🔵 Response data:', data);

    if (data.error) {
      console.error('🔴 API returned error:', data.error);
      throw new Error(data.error);
    }

    return { url: data.url, error: null };
  } catch (error) {
    console.error('🔴 Checkout session error:', error);
    return { url: null, error };
  }
};

// ============================================
// DATA CONVERSION HELPERS
// Convert between Supabase format and app format
// ============================================

export const dbJobToAppJob = (dbJob) => ({
  id: dbJob.id,
  company: dbJob.company,
  role: dbJob.role,
  url: dbJob.url,
  jobPostedDate: dbJob.job_posted_date,
  status: dbJob.status,
  matchScore: dbJob.match_score,
  confidence: dbJob.confidence,
  isTarget: dbJob.is_target,
  analysis: dbJob.analysis,
  gapAnswers: dbJob.gap_answers,
  generatedResume: dbJob.generated_resume,
  rawJd: dbJob.raw_jd,
  suggestedProjects: dbJob.suggested_projects || [],
  addedAt: dbJob.added_at,
  appliedAt: dbJob.applied_at,
  rejectedAtStage: dbJob.rejected_at_stage,
});

// Lightweight converter for summary jobs (no heavy fields)
export const dbJobSummaryToApp = (dbJob) => ({
  id: dbJob.id,
  company: dbJob.company,
  role: dbJob.role,
  url: dbJob.url,
  status: dbJob.status,
  matchScore: dbJob.match_score,
  confidence: dbJob.confidence,
  isTarget: dbJob.is_target,
  addedAt: dbJob.added_at,
  // Mark as summary so UI knows details need lazy loading
  _isSummary: true,
});

export const dbInsightToAppInsight = (dbInsight) => ({
  id: dbInsight.id,
  skill: dbInsight.skill,
  context: dbInsight.context,
  source: dbInsight.source,
  jobContext: dbInsight.job_context,
  timestamp: dbInsight.created_at,
});

export const dbProfileToAppProfile = (dbProfile) => ({
  name: dbProfile.name || '',
  email: dbProfile.email || '',
  title: dbProfile.title || '',
  targetRole: dbProfile.target_role || '',
  currentSalary: dbProfile.current_salary || 0,
  targetSalaryMin: dbProfile.target_salary_min || 0,
  targetSalaryMax: dbProfile.target_salary_max || 0,
  summary: dbProfile.summary || '',
  skills: dbProfile.skills || [],
  experiences: dbProfile.experiences || [],
  currentProjects: dbProfile.current_projects || [],
  education: dbProfile.education || [],
  activities: dbProfile.activities || '',
  subscriptionStatus: dbProfile.subscription_status || 'free',
  roleTracks: dbProfile.role_tracks || [],
  activeTrackId: dbProfile.active_track_id || null,
});

export const appProfileToDbProfile = (appProfile) => {
  // Build profile object, filtering out undefined values
  // Only include fields that are actually set to avoid overwriting with defaults
  const profile = {
    name: appProfile.name || '',
    email: appProfile.email || '',
    title: appProfile.title || '',
    target_role: appProfile.targetRole || '',
    current_salary: appProfile.currentSalary || 0,
    target_salary_min: appProfile.targetSalaryMin || 0,
    target_salary_max: appProfile.targetSalaryMax || 0,
    summary: appProfile.summary || '',
    skills: appProfile.skills || [],
    experiences: appProfile.experiences || [],
    current_projects: appProfile.currentProjects || [],
  };
  // Only include education if explicitly provided (not lazy-loaded)
  if (appProfile.education !== undefined) {
    profile.education = appProfile.education;
  }
  // Only include activities if explicitly provided
  if (appProfile.activities !== undefined) {
    profile.activities = appProfile.activities;
  }
  // Include role tracks if present
  if (appProfile.roleTracks !== undefined) {
    profile.role_tracks = appProfile.roleTracks;
  }
  if (appProfile.activeTrackId !== undefined) {
    profile.active_track_id = appProfile.activeTrackId;
  }
  return profile;
};

// ============================================
// INTERVIEWS HELPERS
// ============================================

export const getInterviews = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('interviews')
      .select('*, jobs(company, role)')
      .eq('user_id', userId)
      .order('scheduled_at', { ascending: true });
    // If table doesn't exist, return empty array
    if (error && error.code === '42P01') {
      console.log('Interviews table not found, returning empty array');
      return { data: [], error: null };
    }
    return { data: data || [], error };
  } catch (e) {
    console.log('Error fetching interviews:', e);
    return { data: [], error: null };
  }
};

export const getInterviewsForJob = async (jobId) => {
  try {
    const { data, error } = await supabase
      .from('interviews')
      .select('*')
      .eq('job_id', jobId)
      .order('scheduled_at', { ascending: true });
    if (error && error.code === '42P01') {
      return { data: [], error: null };
    }
    return { data: data || [], error };
  } catch (e) {
    console.log('Error fetching interviews for job:', e);
    return { data: [], error: null };
  }
};

export const addInterview = async (userId, interview) => {
  const { data, error } = await supabase
    .from('interviews')
    .insert({
      user_id: userId,
      job_id: interview.jobId,
      interview_type: interview.interviewType || 'phone_screen',
      scheduled_at: interview.scheduledAt || null,
      duration_minutes: interview.durationMinutes || 60,
      location: interview.location || null,
      meeting_link: interview.meetingLink || null,
      recruiter_name: interview.recruiterName || null,
      recruiter_email: interview.recruiterEmail || null,
      notes: interview.notes || null,
      status: 'scheduled'
    })
    .select()
    .single();
  return { data, error };
};

export const updateInterview = async (interviewId, updates) => {
  const dbUpdates = {};
  if (updates.interviewType !== undefined) dbUpdates.interview_type = updates.interviewType;
  if (updates.scheduledAt !== undefined) dbUpdates.scheduled_at = updates.scheduledAt;
  if (updates.durationMinutes !== undefined) dbUpdates.duration_minutes = updates.durationMinutes;
  if (updates.location !== undefined) dbUpdates.location = updates.location;
  if (updates.meetingLink !== undefined) dbUpdates.meeting_link = updates.meetingLink;
  if (updates.recruiterName !== undefined) dbUpdates.recruiter_name = updates.recruiterName;
  if (updates.recruiterEmail !== undefined) dbUpdates.recruiter_email = updates.recruiterEmail;
  if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
  if (updates.prepDocument !== undefined) dbUpdates.prep_document = updates.prepDocument;
  if (updates.starExamples !== undefined) dbUpdates.star_examples = updates.starExamples;
  if (updates.questionsToAsk !== undefined) dbUpdates.questions_to_ask = updates.questionsToAsk;
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.outcome !== undefined) dbUpdates.outcome = updates.outcome;
  if (updates.outcomeNotes !== undefined) dbUpdates.outcome_notes = updates.outcomeNotes;
  if (updates.feedback !== undefined) dbUpdates.feedback = updates.feedback;
  if (updates.completedAt !== undefined) dbUpdates.completed_at = updates.completedAt;

  dbUpdates.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from('interviews')
    .update(dbUpdates)
    .eq('id', interviewId)
    .select()
    .single();
  return { data, error };
};

export const deleteInterview = async (interviewId) => {
  const { error } = await supabase
    .from('interviews')
    .delete()
    .eq('id', interviewId);
  return { error };
};

export const dbInterviewToAppInterview = (dbInterview) => ({
  id: dbInterview.id,
  jobId: dbInterview.job_id,
  interviewType: dbInterview.interview_type,
  scheduledAt: dbInterview.scheduled_at,
  durationMinutes: dbInterview.duration_minutes,
  location: dbInterview.location,
  meetingLink: dbInterview.meeting_link,
  recruiterName: dbInterview.recruiter_name,
  recruiterEmail: dbInterview.recruiter_email,
  interviewerNames: dbInterview.interviewer_names || [],
  emailThread: dbInterview.email_thread,
  notes: dbInterview.notes,
  prepDocument: dbInterview.prep_document,
  starExamples: dbInterview.star_examples || [],
  questionsToAsk: dbInterview.questions_to_ask || [],
  status: dbInterview.status,
  outcome: dbInterview.outcome,
  outcomeNotes: dbInterview.outcome_notes,
  feedback: dbInterview.feedback,
  createdAt: dbInterview.created_at,
  updatedAt: dbInterview.updated_at,
  completedAt: dbInterview.completed_at,
  // Joined data from jobs table
  company: dbInterview.jobs?.company,
  role: dbInterview.jobs?.role,
});

// ============================================
// JOB RADAR - TRACKED COMPANIES HELPERS
// ============================================

export const getTrackedCompanies = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('tracked_companies')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error && error.code === '42P01') {
      console.log('Tracked companies table not found, returning empty array');
      return { data: [], error: null };
    }
    return { data: data || [], error };
  } catch (e) {
    console.log('Error fetching tracked companies:', e);
    return { data: [], error: null };
  }
};

export const addTrackedCompany = async (userId, company) => {
  const { data, error } = await supabase
    .from('tracked_companies')
    .insert({
      user_id: userId,
      company_name: company.companyName,
      careers_url: company.careersUrl,
      logo_url: company.logoUrl || null,
      target_roles: company.targetRoles || [],
      track_all_jobs: company.trackAllJobs !== false,
      is_active: true,
    })
    .select()
    .single();
  return { data, error };
};

export const updateTrackedCompany = async (companyId, updates) => {
  const dbUpdates = {};
  if (updates.companyName !== undefined) dbUpdates.company_name = updates.companyName;
  if (updates.careersUrl !== undefined) dbUpdates.careers_url = updates.careersUrl;
  if (updates.logoUrl !== undefined) dbUpdates.logo_url = updates.logoUrl;
  if (updates.targetRoles !== undefined) dbUpdates.target_roles = updates.targetRoles;
  if (updates.trackAllJobs !== undefined) dbUpdates.track_all_jobs = updates.trackAllJobs;
  if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
  if (updates.lastCheckedAt !== undefined) dbUpdates.last_checked_at = updates.lastCheckedAt;
  if (updates.lastJobCount !== undefined) dbUpdates.last_job_count = updates.lastJobCount;

  dbUpdates.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from('tracked_companies')
    .update(dbUpdates)
    .eq('id', companyId)
    .select()
    .single();
  return { data, error };
};

export const deleteTrackedCompany = async (companyId) => {
  const { error } = await supabase
    .from('tracked_companies')
    .delete()
    .eq('id', companyId);
  return { error };
};

export const dbTrackedCompanyToApp = (db) => ({
  id: db.id,
  companyName: db.company_name,
  careersUrl: db.careers_url,
  logoUrl: db.logo_url,
  targetRoles: db.target_roles || [],
  trackAllJobs: db.track_all_jobs,
  isActive: db.is_active,
  lastCheckedAt: db.last_checked_at,
  lastJobCount: db.last_job_count,
  createdAt: db.created_at,
  updatedAt: db.updated_at,
});

// ============================================
// JOB RADAR - JOB ALERTS HELPERS
// ============================================

export const getJobAlerts = async (userId, options = {}) => {
  try {
    let query = supabase
      .from('job_alerts')
      .select('*, tracked_companies(company_name, careers_url, logo_url)')
      .eq('user_id', userId);

    // Filter by status if provided
    if (options.status) {
      query = query.eq('status', options.status);
    }

    // Filter unread only
    if (options.unreadOnly) {
      query = query.eq('is_read', false);
    }

    // Limit results
    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query.order('discovered_at', { ascending: false });

    if (error && error.code === '42P01') {
      console.log('Job alerts table not found, returning empty array');
      return { data: [], error: null };
    }
    return { data: data || [], error };
  } catch (e) {
    console.log('Error fetching job alerts:', e);
    return { data: [], error: null };
  }
};

export const getJobAlertsForCompany = async (trackedCompanyId) => {
  try {
    const { data, error } = await supabase
      .from('job_alerts')
      .select('*')
      .eq('tracked_company_id', trackedCompanyId)
      .order('discovered_at', { ascending: false });
    if (error && error.code === '42P01') {
      return { data: [], error: null };
    }
    return { data: data || [], error };
  } catch (e) {
    console.log('Error fetching job alerts for company:', e);
    return { data: [], error: null };
  }
};

export const addJobAlert = async (userId, alert) => {
  const { data, error } = await supabase
    .from('job_alerts')
    .insert({
      user_id: userId,
      tracked_company_id: alert.trackedCompanyId,
      job_title: alert.jobTitle,
      job_url: alert.jobUrl,
      location: alert.location,
      job_type: alert.jobType,
      department: alert.department,
      is_target_match: alert.isTargetMatch || false,
      match_reason: alert.matchReason,
      posted_at: alert.postedAt,
      expires_at: alert.expiresAt,
    })
    .select()
    .single();
  return { data, error };
};

export const updateJobAlert = async (alertId, updates) => {
  const dbUpdates = {};
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.isRead !== undefined) dbUpdates.is_read = updates.isRead;

  const { data, error } = await supabase
    .from('job_alerts')
    .update(dbUpdates)
    .eq('id', alertId)
    .select()
    .single();
  return { data, error };
};

export const markAlertsAsRead = async (alertIds) => {
  const { data, error } = await supabase
    .from('job_alerts')
    .update({ is_read: true })
    .in('id', alertIds)
    .select();
  return { data, error };
};

export const deleteJobAlert = async (alertId) => {
  const { error } = await supabase
    .from('job_alerts')
    .delete()
    .eq('id', alertId);
  return { error };
};

export const getUnreadAlertCount = async (userId) => {
  try {
    const { count, error } = await supabase
      .from('job_alerts')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_read', false);
    if (error && error.code === '42P01') {
      return { count: 0, error: null };
    }
    return { count: count || 0, error };
  } catch (e) {
    return { count: 0, error: null };
  }
};

export const dbJobAlertToApp = (db) => ({
  id: db.id,
  trackedCompanyId: db.tracked_company_id,
  jobTitle: db.job_title,
  jobUrl: db.job_url,
  location: db.location,
  jobType: db.job_type,
  department: db.department,
  isTargetMatch: db.is_target_match,
  matchReason: db.match_reason,
  status: db.status,
  isRead: db.is_read,
  discoveredAt: db.discovered_at,
  postedAt: db.posted_at,
  expiresAt: db.expires_at,
  // Joined data
  companyName: db.tracked_companies?.company_name,
  careersUrl: db.tracked_companies?.careers_url,
  logoUrl: db.tracked_companies?.logo_url,
});

// Check if user can access Job Radar (Pro only)
export const canAccessJobRadar = async (userId) => {
  const { data, error } = await getSubscriptionInfo(userId);
  if (error) return { canAccess: false, subscriptionStatus: 'free' };

  const subscriptionStatus = data?.subscription_status || 'free';
  const effectiveTier = getEffectiveSubscription(subscriptionStatus);

  // Job Radar requires Pro
  return {
    canAccess: effectiveTier === 'pro',
    subscriptionStatus,
    effectiveTier,
    requiredTier: 'pro'
  };
};

// ============================================
// BRAG BOOK HELPERS
// ============================================

export const getBragBookEntries = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('brag_book_entries')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error && error.code === '42P01') {
      console.log('Brag book table not found, returning empty array');
      return { data: [], error: null };
    }
    return { data: data || [], error };
  } catch (e) {
    console.log('Error fetching brag book entries:', e);
    return { data: [], error: null };
  }
};

export const addBragBookEntry = async (userId, entry) => {
  const { data, error } = await supabase
    .from('brag_book_entries')
    .insert({
      user_id: userId,
      title: entry.title,
      company: entry.company || null,
      date: entry.date || null,
      situation: entry.situation || null,
      task: entry.task || null,
      action: entry.action || null,
      result: entry.result || null,
      skills: entry.skills || [],
      impact: entry.impact || null,
    })
    .select()
    .single();
  return { data, error };
};

export const updateBragBookEntry = async (entryId, updates) => {
  const dbUpdates = {
    updated_at: new Date().toISOString(),
  };
  if (updates.title !== undefined) dbUpdates.title = updates.title;
  if (updates.company !== undefined) dbUpdates.company = updates.company;
  if (updates.date !== undefined) dbUpdates.date = updates.date;
  if (updates.situation !== undefined) dbUpdates.situation = updates.situation;
  if (updates.task !== undefined) dbUpdates.task = updates.task;
  if (updates.action !== undefined) dbUpdates.action = updates.action;
  if (updates.result !== undefined) dbUpdates.result = updates.result;
  if (updates.skills !== undefined) dbUpdates.skills = updates.skills;
  if (updates.impact !== undefined) dbUpdates.impact = updates.impact;

  const { data, error } = await supabase
    .from('brag_book_entries')
    .update(dbUpdates)
    .eq('id', entryId)
    .select()
    .single();
  return { data, error };
};

export const deleteBragBookEntry = async (entryId) => {
  const { error } = await supabase
    .from('brag_book_entries')
    .delete()
    .eq('id', entryId);
  return { error };
};

export const dbBragBookEntryToApp = (db) => ({
  id: db.id,
  title: db.title,
  company: db.company,
  date: db.date,
  situation: db.situation,
  task: db.task,
  action: db.action,
  result: db.result,
  skills: db.skills || [],
  impact: db.impact,
  createdAt: db.created_at,
  updatedAt: db.updated_at,
});

// ============================================
// COMPANY RESEARCH CACHE HELPERS
// ============================================

const normalizeCompanyName = (name) => {
  return name?.toLowerCase().trim().replace(/[^a-z0-9]/g, '') || '';
};

export const getCompanyResearch = async (companyName) => {
  const normalized = normalizeCompanyName(companyName);
  if (!normalized) return { data: null, error: null };

  try {
    const { data, error } = await supabase
      .from('company_research')
      .select('*')
      .eq('company_name_normalized', normalized)
      .gt('expires_at', new Date().toISOString())
      .single();

    if (error && error.code === 'PGRST116') {
      // No rows found - not an error, just no cache
      return { data: null, error: null };
    }
    if (error && error.code === '42P01') {
      // Table doesn't exist yet
      console.log('Company research table not found');
      return { data: null, error: null };
    }
    return { data, error };
  } catch (e) {
    console.log('Error fetching company research:', e);
    return { data: null, error: null };
  }
};

export const saveCompanyResearch = async (companyName, marketAnalysis, companyBrief) => {
  const normalized = normalizeCompanyName(companyName);
  if (!normalized) return { data: null, error: 'Invalid company name' };

  try {
    const { data, error } = await supabase
      .from('company_research')
      .upsert({
        company_name: companyName,
        company_name_normalized: normalized,
        market_analysis: marketAnalysis,
        company_brief: companyBrief,
        updated_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
      }, {
        onConflict: 'company_name_normalized'
      })
      .select()
      .single();

    return { data, error };
  } catch (e) {
    console.log('Error saving company research:', e);
    return { data: null, error: e };
  }
};

export const dbCompanyResearchToApp = (db) => ({
  id: db.id,
  companyName: db.company_name,
  marketAnalysis: db.market_analysis,
  companyBrief: db.company_brief,
  confidenceScore: db.confidence_score,
  createdAt: db.created_at,
  updatedAt: db.updated_at,
  expiresAt: db.expires_at,
});

// ============================================
// DAILY MATCH - SWIPE QUEUE HELPERS
// ============================================

const FREE_SWIPE_LIMIT = 10;    // 10 swipes/day for free users
// Note: Apply limit (right swipes) uses existing resume generation limit (FREE_RESUME_LIMIT)

/**
 * Get today's swipe queue for a user
 */
export const getSwipeQueue = async (userId) => {
  const today = new Date().toISOString().split('T')[0];

  try {
    const { data, error } = await supabase
      .from('swipe_queue')
      .select('*')
      .eq('user_id', userId)
      .eq('queue_date', today)
      .eq('status', 'pending')
      .order('position', { ascending: true });

    if (error && error.code === '42P01') {
      console.log('Swipe queue table not found, returning empty array');
      return { data: [], error: null };
    }
    return { data: data || [], error };
  } catch (e) {
    console.log('Error fetching swipe queue:', e);
    return { data: [], error: null };
  }
};

/**
 * Get swipe queue stats for today
 */
export const getSwipeStats = async (userId) => {
  const today = new Date().toISOString().split('T')[0];

  try {
    const { data, error } = await supabase
      .from('swipe_queue')
      .select('status')
      .eq('user_id', userId)
      .eq('queue_date', today);

    if (error && error.code === '42P01') {
      return { stats: { total: 0, pending: 0, applied: 0, passed: 0, deferred: 0 }, error: null };
    }

    const stats = {
      total: data?.length || 0,
      pending: data?.filter(i => i.status === 'pending').length || 0,
      applied: data?.filter(i => i.status === 'applied').length || 0,
      passed: data?.filter(i => i.status === 'passed').length || 0,
      deferred: data?.filter(i => i.status === 'deferred').length || 0,
    };

    return { stats, error: null };
  } catch (e) {
    console.log('Error fetching swipe stats:', e);
    return { stats: { total: 0, pending: 0, applied: 0, passed: 0, deferred: 0 }, error: null };
  }
};

/**
 * Check if user can swipe today (based on daily limit)
 */
export const canUserSwipe = async (userId) => {
  const { data, error } = await getSubscriptionInfo(userId);

  if (error) {
    console.error('Error checking subscription for swipe:', error);
    return { canSwipe: false, remaining: 0, subscriptionStatus: 'free' };
  }

  const subscriptionStatus = data?.subscription_status || 'free';
  const effectiveTier = getEffectiveSubscription(subscriptionStatus);
  const isAdmin = data?.is_admin === true;
  const today = new Date().toISOString().split('T')[0];

  // Admin and Pro users have unlimited swipes
  if (isAdmin || effectiveTier === 'pro') {
    return { canSwipe: true, remaining: -1, subscriptionStatus, effectiveTier, isAdmin };
  }

  // Check daily usage from profile
  let swipesToday = data?.swipes_today || 0;
  const resetDate = data?.swipe_reset_date;

  // Reset if new day
  if (resetDate !== today) {
    swipesToday = 0;
  }

  const remaining = Math.max(0, FREE_SWIPE_LIMIT - swipesToday);
  return {
    canSwipe: remaining > 0,
    remaining,
    subscriptionStatus,
    effectiveTier,
    isAdmin,
    swipesToday,
  };
};

/**
 * Check if user can apply (right swipe) today
 */
export const canUserApply = async (userId) => {
  // Apply limit is tied to resume generation limit
  return await canUserGenerateResume(userId);
};

/**
 * Record a swipe action
 */
export const recordSwipe = async (userId, queueItemId, direction, jobData = {}) => {
  const today = new Date().toISOString().split('T')[0];
  const now = new Date().toISOString();

  // Map direction to status
  const statusMap = {
    'right': 'applied',
    'left': 'passed',
    'up': 'deferred',
  };

  const newStatus = statusMap[direction];

  try {
    // 1. Update the queue item
    const { data: queueItem, error: queueError } = await supabase
      .from('swipe_queue')
      .update({
        status: newStatus,
        swiped_at: now,
      })
      .eq('id', queueItemId)
      .eq('user_id', userId)
      .select()
      .single();

    if (queueError) {
      console.error('Error updating swipe queue:', queueError);
      return { error: queueError };
    }

    // 2. Record in swipe history
    const historyEntry = {
      user_id: userId,
      job_id: queueItem.job_id || null,
      company: jobData.company || queueItem.external_job_data?.company,
      role: jobData.role || jobData.title || queueItem.external_job_data?.title,
      job_url: queueItem.source_url,
      direction,
      match_score: queueItem.pre_match_score,
      source: queueItem.source,
      resume_generated: direction === 'right',
    };

    const { error: historyError } = await supabase
      .from('swipe_history')
      .insert(historyEntry);

    if (historyError && historyError.code !== '42P01') {
      console.error('Error recording swipe history:', historyError);
    }

    // 3. Update daily swipe count in profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('swipes_today, swipe_reset_date')
      .eq('id', userId)
      .single();

    let newSwipeCount = 1;
    if (profile) {
      const resetDate = profile.swipe_reset_date;
      if (resetDate === today) {
        newSwipeCount = (profile.swipes_today || 0) + 1;
      }
    }

    await supabase
      .from('profiles')
      .update({
        swipes_today: newSwipeCount,
        swipe_reset_date: today,
      })
      .eq('id', userId);

    return { data: queueItem, status: newStatus, error: null };

  } catch (e) {
    console.error('Error recording swipe:', e);
    return { error: e };
  }
};

/**
 * Get swipe history for analytics
 */
export const getSwipeHistory = async (userId, options = {}) => {
  try {
    let query = supabase
      .from('swipe_history')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (options.direction) {
      query = query.eq('direction', options.direction);
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error && error.code === '42P01') {
      return { data: [], error: null };
    }
    return { data: data || [], error };
  } catch (e) {
    console.log('Error fetching swipe history:', e);
    return { data: [], error: null };
  }
};

/**
 * Get swipe analytics (patterns, trends)
 */
export const getSwipeAnalytics = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('swipe_history')
      .select('direction, match_score, source, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(100);

    if (error && error.code === '42P01') {
      return { analytics: null, error: null };
    }

    if (!data || data.length === 0) {
      return { analytics: null, error: null };
    }

    // Calculate analytics
    const total = data.length;
    const applied = data.filter(s => s.direction === 'right').length;
    const passed = data.filter(s => s.direction === 'left').length;
    const deferred = data.filter(s => s.direction === 'up').length;

    const appliedScores = data
      .filter(s => s.direction === 'right' && s.match_score)
      .map(s => s.match_score);

    const passedScores = data
      .filter(s => s.direction === 'left' && s.match_score)
      .map(s => s.match_score);

    const analytics = {
      total,
      applied,
      passed,
      deferred,
      applyRate: total > 0 ? (applied / total * 100).toFixed(1) : 0,
      avgAppliedScore: appliedScores.length > 0
        ? (appliedScores.reduce((a, b) => a + b, 0) / appliedScores.length).toFixed(0)
        : null,
      avgPassedScore: passedScores.length > 0
        ? (passedScores.reduce((a, b) => a + b, 0) / passedScores.length).toFixed(0)
        : null,
      sourceBreakdown: data.reduce((acc, s) => {
        acc[s.source] = (acc[s.source] || 0) + 1;
        return acc;
      }, {}),
    };

    return { analytics, error: null };
  } catch (e) {
    console.log('Error fetching swipe analytics:', e);
    return { analytics: null, error: null };
  }
};

/**
 * Convert DB swipe queue item to app format
 */
export const dbSwipeQueueItemToApp = (db) => ({
  id: db.id,
  userId: db.user_id,
  jobId: db.job_id,
  externalJobData: db.external_job_data,
  source: db.source,
  sourceUrl: db.source_url,
  preMatchScore: db.pre_match_score,
  preMatchHighlights: db.pre_match_highlights || [],
  preMatchConcerns: db.pre_match_concerns || [],
  queueDate: db.queue_date,
  position: db.position,
  status: db.status,
  createdAt: db.created_at,
  swipedAt: db.swiped_at,
});

/**
 * Convert DB swipe history item to app format
 */
export const dbSwipeHistoryToApp = (db) => ({
  id: db.id,
  jobId: db.job_id,
  company: db.company,
  role: db.role,
  jobUrl: db.job_url,
  direction: db.direction,
  matchScore: db.match_score,
  source: db.source,
  resumeGenerated: db.resume_generated,
  createdAt: db.created_at,
});

// ============================================
// GMAIL INTEGRATION HELPERS
// ============================================

/**
 * Connect Gmail via Google OAuth
 * Requests gmail.readonly scope to read job-related emails
 */
export const connectGmail = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      scopes: 'https://www.googleapis.com/auth/gmail.readonly',
      redirectTo: `${window.location.origin}/?gmail_connected=true`,
      queryParams: {
        access_type: 'offline',
        prompt: 'consent'
      }
    }
  });
  return { data, error };
};

/**
 * Disconnect Gmail by removing the provider token
 * Note: User can still be logged in, we just mark gmail as disconnected
 */
export const disconnectGmail = async (userId) => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        gmail_connected: false,
        gmail_last_sync: null,
        gmail_history_id: null,
      })
      .eq('id', userId);
    return { error };
  } catch (e) {
    console.error('Error disconnecting Gmail:', e);
    return { error: e };
  }
};

/**
 * Update Gmail connection status in profile
 */
export const updateGmailConnection = async (userId, connected, lastSync = null) => {
  const updates = {
    gmail_connected: connected,
  };
  if (lastSync) {
    updates.gmail_last_sync = lastSync;
  }

  try {
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId);
    return { error };
  } catch (e) {
    console.error('Error updating Gmail connection:', e);
    return { error: e };
  }
};

/**
 * Get application emails for a user
 */
export const getApplicationEmails = async (userId, options = {}) => {
  try {
    let query = supabase
      .from('application_emails')
      .select('*')
      .eq('user_id', userId)
      .eq('is_archived', false);

    // Filter by email type
    if (options.emailType && options.emailType !== 'all') {
      query = query.eq('email_type', options.emailType);
    }

    // Filter unread only
    if (options.unreadOnly) {
      query = query.eq('is_read', false);
    }

    // Filter by matched company
    if (options.company) {
      query = query.eq('matched_company', options.company);
    }

    // Filter by matched job
    if (options.jobId) {
      query = query.eq('matched_job_id', options.jobId);
    }

    // Limit results
    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query.order('date_received', { ascending: false });

    if (error && error.code === '42P01') {
      console.log('Application emails table not found, returning empty array');
      return { data: [], error: null };
    }
    return { data: data || [], error };
  } catch (e) {
    console.log('Error fetching application emails:', e);
    return { data: [], error: null };
  }
};

/**
 * Add an application email to the database
 */
export const addApplicationEmail = async (userId, email) => {
  try {
    const { data, error } = await supabase
      .from('application_emails')
      .insert({
        user_id: userId,
        gmail_message_id: email.gmailMessageId,
        gmail_thread_id: email.gmailThreadId,
        subject: email.subject,
        from_email: email.fromEmail,
        from_name: email.fromName || null,
        to_email: email.toEmail || null,
        date_received: email.dateReceived,
        snippet: email.snippet || null,
        body_preview: email.bodyPreview || null,
        body_html: email.bodyHtml || null,
        email_type: email.emailType || 'unknown',
        confidence_score: email.confidenceScore || 0,
        matched_job_id: email.matchedJobId || null,
        matched_company: email.matchedCompany || null,
      })
      .select()
      .single();
    return { data, error };
  } catch (e) {
    console.error('Error adding application email:', e);
    return { data: null, error: e };
  }
};

/**
 * Update email read status
 */
export const updateEmailRead = async (emailId, isRead = true) => {
  try {
    const { data, error } = await supabase
      .from('application_emails')
      .update({ is_read: isRead })
      .eq('id', emailId)
      .select()
      .single();
    return { data, error };
  } catch (e) {
    console.error('Error updating email read status:', e);
    return { data: null, error: e };
  }
};

/**
 * Archive an email
 */
export const archiveEmail = async (emailId) => {
  try {
    const { data, error } = await supabase
      .from('application_emails')
      .update({ is_archived: true })
      .eq('id', emailId)
      .select()
      .single();
    return { data, error };
  } catch (e) {
    console.error('Error archiving email:', e);
    return { data: null, error: e };
  }
};

/**
 * Link an email to a specific job
 */
export const linkEmailToJob = async (emailId, jobId, company = null) => {
  try {
    const updates = { matched_job_id: jobId };
    if (company) {
      updates.matched_company = company;
    }

    const { data, error } = await supabase
      .from('application_emails')
      .update(updates)
      .eq('id', emailId)
      .select()
      .single();
    return { data, error };
  } catch (e) {
    console.error('Error linking email to job:', e);
    return { data: null, error: e };
  }
};

/**
 * Get unread email count
 */
export const getUnreadEmailCount = async (userId) => {
  try {
    const { count, error } = await supabase
      .from('application_emails')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_read', false)
      .eq('is_archived', false);

    if (error && error.code === '42P01') {
      return { count: 0, error: null };
    }
    return { count: count || 0, error };
  } catch (e) {
    return { count: 0, error: null };
  }
};

/**
 * Convert DB application email to app format
 */
export const dbApplicationEmailToApp = (db) => ({
  id: db.id,
  gmailMessageId: db.gmail_message_id,
  gmailThreadId: db.gmail_thread_id,
  subject: db.subject,
  fromEmail: db.from_email,
  fromName: db.from_name,
  toEmail: db.to_email,
  dateReceived: db.date_received,
  snippet: db.snippet,
  bodyPreview: db.body_preview,
  bodyHtml: db.body_html,
  emailType: db.email_type,
  confidenceScore: db.confidence_score,
  matchedJobId: db.matched_job_id,
  matchedCompany: db.matched_company,
  isRead: db.is_read,
  isArchived: db.is_archived,
  createdAt: db.created_at,
  syncedAt: db.synced_at,
});
